/**
 * LayerCreator Service
 *
 * Manages layer creation, feature digitization, snapping, and copy/paste operations.
 * Uses OpenLayers Draw and Snap interactions for map-based feature creation.
 *
 * Validates: Requirements 47.1, 48.1, 48.5, 48.6, 49.1, 49.2, 49.3, 49.4, 49.5, 49.6
 */
import Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import Feature from 'ol/Feature';
import { GeoJSON as GeoJSONFormat } from 'ol/format';
import type { Geometry } from 'ol/geom';
import type {
  CreateLayerConfig,
  AttributeField,
  SnappingConfig,
  ClipboardFeature,
  PasteResult,
  LayerGeometryType,
  DigitizationMode,
} from '../types/layerCreation';
import { DEFAULT_SNAPPING_CONFIG } from '../types/layerCreation';
import { validateGeometry, type ValidatableGeometry } from '../utils/validateGeometry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DrawEndCallback = (geometry: GeoJSON.Geometry) => void;
export type ModeChangeCallback = (mode: DigitizationMode) => void;

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

export class LayerCreatorService {
  private map: Map | null = null;
  private drawInteraction: Draw | null = null;
  private snapInteractions: Snap[] = [];
  private snappingConfig: SnappingConfig = { ...DEFAULT_SNAPPING_CONFIG };
  private clipboard: ClipboardFeature[] = [];
  private onDrawEnd: DrawEndCallback | null = null;
  private onModeChange: ModeChangeCallback | null = null;
  private geoJsonFormat: GeoJSONFormat;
  private currentMode: DigitizationMode = 'idle';

  constructor() {
    this.geoJsonFormat = new GeoJSONFormat();
  }

  // ─── Initialization ──────────────────────────────────────────────────────

  /**
   * Initialize the service with a map instance.
   */
  initialize(map: Map): void {
    this.map = map;
  }

  /**
   * Clean up all interactions.
   */
  dispose(): void {
    this.deactivateDigitization();
    this.disableSnapping();
    this.map = null;
  }

  // ─── Event Callbacks ─────────────────────────────────────────────────────

  setOnDrawEnd(callback: DrawEndCallback | null): void {
    this.onDrawEnd = callback;
  }

  setOnModeChange(callback: ModeChangeCallback | null): void {
    this.onModeChange = callback;
  }

  // ─── Layer Creation (Task 30.1, 30.2) ────────────────────────────────────

  /**
   * Create an empty layer configuration. The actual layer creation is handled
   * by the Redux thunk (createLayerAsync) which calls the backend API.
   * This method validates the configuration before submission.
   */
  validateCreateLayerConfig(config: CreateLayerConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push('Layer name is required');
    }

    if (!['Point', 'LineString', 'Polygon'].includes(config.geometryType)) {
      errors.push('Invalid geometry type. Must be Point, LineString, or Polygon');
    }

    if (!config.crs || config.crs.trim().length === 0) {
      errors.push('CRS is required');
    }

    // Validate attribute fields
    const fieldNames = new Set<string>();
    for (const field of config.attributeFields) {
      if (!field.name || field.name.trim().length === 0) {
        errors.push('Attribute field name cannot be empty');
      } else if (fieldNames.has(field.name)) {
        errors.push(`Duplicate attribute field name: ${field.name}`);
      } else {
        fieldNames.add(field.name);
      }

      if (!['string', 'number', 'boolean', 'date'].includes(field.type)) {
        errors.push(`Invalid field type for "${field.name}": ${field.type}`);
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Get default attribute values for a set of fields.
   */
  getDefaultAttributes(fields: AttributeField[]): Record<string, any> {
    const defaults: Record<string, any> = {};
    for (const field of fields) {
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        defaults[field.name] = field.defaultValue;
      } else {
        switch (field.type) {
          case 'string':
            defaults[field.name] = '';
            break;
          case 'number':
            defaults[field.name] = 0;
            break;
          case 'boolean':
            defaults[field.name] = false;
            break;
          case 'date':
            defaults[field.name] = null;
            break;
        }
      }
    }
    return defaults;
  }

  /**
   * Check if digitization is currently active.
   */
  isDigitizing(): boolean {
    return this.drawInteraction !== null;
  }

  /**
   * Activate digitization mode for a specific geometry type.
   * Creates a Draw interaction matching the layer's geometry type.
   */
  activateDigitization(geometryType: LayerGeometryType, targetSource?: VectorSource): void {
    if (!this.map) return;

    this.deactivateDigitization();

    const drawOptions: any = {
      type: geometryType,
    };

    if (targetSource) {
      drawOptions.source = targetSource;
    }

    this.drawInteraction = new Draw(drawOptions);

    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      const geometry = feature.getGeometry();
      if (!geometry) return;

      const geojsonGeometry = JSON.parse(
        this.geoJsonFormat.writeGeometry(geometry, {
          dataProjection: 'EPSG:4326',
          featureProjection: this.map!.getView().getProjection(),
        })
      );

      this.setMode('attribute-input');
      this.onDrawEnd?.(geojsonGeometry);

      // Remove the feature from the source if it was auto-added
      // (we'll add it properly after attribute input)
      if (targetSource) {
        targetSource.removeFeature(feature);
      }
    });

    this.map.addInteraction(this.drawInteraction);
    this.setMode('drawing');

    // Re-enable snapping if configured
    if (this.snappingConfig.enabled) {
      this.applySnapping();
    }
  }

  /**
   * Deactivate digitization mode.
   */
  deactivateDigitization(): void {
    if (!this.map) return;

    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }

    this.setMode('idle');
  }

  /**
   * Get the current digitization mode.
   */
  getMode(): DigitizationMode {
    return this.currentMode;
  }

  private setMode(mode: DigitizationMode): void {
    this.currentMode = mode;
    this.onModeChange?.(mode);
  }

  // ─── Geometry Validation (Task 30.10) ────────────────────────────────────

  /**
   * Validate geometry topology before saving.
   * Checks for self-intersections, valid rings, minimum vertex counts.
   */
  validateGeometryTopology(geometry: GeoJSON.Geometry): { valid: boolean; errors: string[] } {
    return validateGeometry(geometry as ValidatableGeometry);
  }

  // ─── Snapping (Task 30.11, 30.12, 30.13) ────────────────────────────────

  /**
   * Enable snapping with the given configuration.
   * Snaps to all visible vector layers on the map.
   */
  enableSnapping(config: SnappingConfig): void {
    this.snappingConfig = { ...config };
    if (config.enabled) {
      this.applySnapping();
    } else {
      this.removeSnapping();
    }
  }

  /**
   * Disable snapping.
   */
  disableSnapping(): void {
    this.snappingConfig = { ...DEFAULT_SNAPPING_CONFIG, enabled: false };
    this.removeSnapping();
  }

  /**
   * Get current snapping configuration.
   */
  getSnappingConfig(): SnappingConfig {
    return { ...this.snappingConfig };
  }

  /**
   * Update snapping tolerance.
   */
  setSnappingTolerance(tolerance: number): void {
    this.snappingConfig.tolerance = tolerance;
    if (this.snappingConfig.enabled) {
      this.removeSnapping();
      this.applySnapping();
    }
  }

  private applySnapping(): void {
    this.removeSnapping();
    if (!this.map) return;

    // Collect all visible vector sources
    const layers = this.map.getLayers().getArray();
    for (const layer of layers) {
      if (layer instanceof VectorLayer && layer.getVisible()) {
        const source = layer.getSource();
        if (source) {
          const snapInteraction = new Snap({
            source: source,
            pixelTolerance: this.snappingConfig.tolerance,
            vertex: this.snappingConfig.snapToVertices,
            edge: this.snappingConfig.snapToEdges,
          });
          this.map.addInteraction(snapInteraction);
          this.snapInteractions.push(snapInteraction);
        }
      }
    }
  }

  private removeSnapping(): void {
    if (this.map) {
      for (const snapInteraction of this.snapInteractions) {
        this.map.removeInteraction(snapInteraction);
      }
    }
    this.snapInteractions = [];
  }

  // ─── Copy/Paste (Task 30.14, 30.15, 30.16, 30.17, 30.18) ───────────────

  /**
   * Copy features to the internal clipboard.
   * Stores geometry and properties for later paste operations.
   */
  copyFeatures(
    features: Array<{ geometry: GeoJSON.Geometry; properties: Record<string, any> }>,
    sourceLayerId: string,
    sourceGeometryType: LayerGeometryType
  ): number {
    this.clipboard = features.map((f) => ({
      geometry: JSON.parse(JSON.stringify(f.geometry)),
      properties: JSON.parse(JSON.stringify(f.properties)),
      sourceLayerId,
      sourceGeometryType,
    }));
    return this.clipboard.length;
  }

  /**
   * Check if there are features in the clipboard.
   */
  hasClipboardContent(): boolean {
    return this.clipboard.length > 0;
  }

  /**
   * Get the number of features in the clipboard.
   */
  getClipboardCount(): number {
    return this.clipboard.length;
  }

  /**
   * Get clipboard features (for inspection/preview).
   */
  getClipboardFeatures(): ClipboardFeature[] {
    return [...this.clipboard];
  }

  /**
   * Paste features to a target layer.
   * Handles geometry type compatibility and attribute field matching.
   *
   * @param targetGeometryType - The geometry type of the target layer
   * @param targetFields - The attribute fields defined on the target layer
   * @returns PasteResult with success status and details
   */
  pasteFeatures(
    targetGeometryType: LayerGeometryType,
    targetFields: AttributeField[]
  ): PasteResult {
    if (this.clipboard.length === 0) {
      return { success: false, pastedCount: 0, skippedCount: 0, warnings: ['Clipboard is empty'] };
    }

    const pastedFeatures: Array<{ geometry: GeoJSON.Geometry; properties: Record<string, any> }> =
      [];
    const warnings: string[] = [];
    let skippedCount = 0;

    for (const clipFeature of this.clipboard) {
      // Check geometry type compatibility
      if (!this.isGeometryCompatible(clipFeature.geometry.type, targetGeometryType)) {
        skippedCount++;
        warnings.push(
          `Skipped feature: incompatible geometry type "${clipFeature.geometry.type}" for target layer type "${targetGeometryType}"`
        );
        continue;
      }

      // Map attributes to target fields
      const mappedProperties = this.mapAttributes(clipFeature.properties, targetFields);

      pastedFeatures.push({
        geometry: JSON.parse(JSON.stringify(clipFeature.geometry)),
        properties: mappedProperties,
      });
    }

    if (pastedFeatures.length === 0) {
      return {
        success: false,
        pastedCount: 0,
        skippedCount,
        warnings: warnings.length > 0 ? warnings : ['No compatible features to paste'],
      };
    }

    return {
      success: true,
      pastedCount: pastedFeatures.length,
      skippedCount,
      warnings,
    };
  }

  /**
   * Get the features that would be pasted (after compatibility filtering).
   * Used by the Redux thunk to actually create the features.
   */
  getPasteableFeatures(
    targetGeometryType: LayerGeometryType,
    targetFields: AttributeField[]
  ): Array<{ geometry: GeoJSON.Geometry; properties: Record<string, any> }> {
    const result: Array<{ geometry: GeoJSON.Geometry; properties: Record<string, any> }> = [];

    for (const clipFeature of this.clipboard) {
      if (!this.isGeometryCompatible(clipFeature.geometry.type, targetGeometryType)) {
        continue;
      }

      const mappedProperties = this.mapAttributes(clipFeature.properties, targetFields);

      result.push({
        geometry: JSON.parse(JSON.stringify(clipFeature.geometry)),
        properties: mappedProperties,
      });
    }

    return result;
  }

  /**
   * Check if a source geometry type is compatible with a target layer type.
   */
  isGeometryCompatible(sourceType: string, targetType: LayerGeometryType): boolean {
    // Exact match
    if (sourceType === targetType) return true;

    // Multi variants are compatible with their single counterparts
    if (sourceType === 'MultiPoint' && targetType === 'Point') return true;
    if (sourceType === 'MultiLineString' && targetType === 'LineString') return true;
    if (sourceType === 'MultiPolygon' && targetType === 'Polygon') return true;

    return false;
  }

  /**
   * Map source attributes to target fields.
   * Matching fields preserve values; missing fields get defaults.
   */
  private mapAttributes(
    sourceProperties: Record<string, any>,
    targetFields: AttributeField[]
  ): Record<string, any> {
    const result: Record<string, any> = {};

    for (const field of targetFields) {
      if (field.name in sourceProperties) {
        // Matching field found - attempt type coercion
        result[field.name] = this.coerceValue(sourceProperties[field.name], field.type);
      } else {
        // No matching field - use default value
        result[field.name] = this.getFieldDefault(field);
      }
    }

    return result;
  }

  /**
   * Coerce a value to the expected field type.
   */
  private coerceValue(value: any, targetType: string): any {
    if (value === null || value === undefined) {
      return this.getTypeDefault(targetType);
    }

    switch (targetType) {
      case 'string':
        return String(value);
      case 'number': {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      }
      case 'boolean':
        return Boolean(value);
      case 'date':
        return value instanceof Date ? value.toISOString() : String(value);
      default:
        return value;
    }
  }

  private getFieldDefault(field: AttributeField): any {
    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      return field.defaultValue;
    }
    return this.getTypeDefault(field.type);
  }

  private getTypeDefault(type: string): any {
    switch (type) {
      case 'string':
        return '';
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return null;
      default:
        return null;
    }
  }
}

// Singleton instance
export const layerCreatorService = new LayerCreatorService();
